import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { lumi } from '@/lib/lumi'

import {Mail, Lock, User, Loader2, Sparkles} from 'lucide-react'

function Login() {
  const navigate = useNavigate()
  const [isLogin, setIsLogin] = useState(true)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [username, setUsername] = useState('')
  const [otp, setOtp] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [otpCooldown, setOtpCooldown] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const startCooldown = () => {
    setOtpCooldown(60)
    const interval = setInterval(() => {
      setOtpCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleSendOtp = async () => {
    if (!email) {
      setError('Please enter your email')
      return
    }
    setError('')
    setLoading(true)
    try {
      await lumi.auth.sendOtp({ 
        email, 
        type: isLogin ? 'resetPassword' : 'register' 
      })
      setOtpSent(true)
      startCooldown()
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP')
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !password) {
      setError('Please fill in all fields')
      return
    }
    setError('')
    setLoading(true)
    try {
      await lumi.auth.signInWithPassword({ email, password })
      navigate('/game')
    } catch (err: any) {
      setError(err.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !password || !username || !otp) {
      setError('Please fill in all fields and request OTP')
      return
    }
    setError('')
    setLoading(true)
    try {
      await lumi.auth.signUpWithPassword({ 
        email, 
        password, 
        otp,
        userName: username 
      })
      navigate('/profile')
    } catch (err: any) {
      setError(err.message || 'Signup failed')
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    setError('')
    try {
      await lumi.auth.signInWithOAuth({ provider: 'google' })
      navigate('/game')
    } catch (err: any) {
      setError(err.message || 'Google login failed')
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0118] via-[#0f0520] to-[#1a0a2e] flex items-center justify-center p-4 overflow-hidden relative">
      {/* Animated stars */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(150)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full animate-pulse"
            style={{
              width: Math.random() * 3 + 0.5 + 'px',
              height: Math.random() * 3 + 0.5 + 'px',
              left: Math.random() * 100 + '%',
              top: Math.random() * 100 + '%',
              animationDelay: Math.random() * 3 + 's',
              animationDuration: Math.random() * 2 + 2 + 's',
              opacity: Math.random() * 0.7 + 0.3,
              boxShadow: `0 0 ${Math.random() * 4 + 2}px rgba(255,255,255,${Math.random() * 0.6})`
            }}
          />
        ))}
      </div>

      {/* Nebula effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
        <div className="absolute w-96 h-96 rounded-full blur-3xl bg-purple-500/50 left-1/4 top-1/4 animate-pulse" style={{ animationDuration: '8s' }} />
        <div className="absolute w-80 h-80 rounded-full blur-3xl bg-blue-500/40 right-1/4 bottom-1/4 animate-pulse" style={{ animationDuration: '10s' }} />
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src="https://static.lumi.new/a1/a17bbbd6146ddf5f97d0afdf148dba2d.webp"
            alt="Rocket Bunny Logo"
            className="w-auto h-32 mx-auto mb-4 drop-shadow-2xl"
          />
          <p className="text-gray-300 font-semibold flex items-center justify-center gap-2">
            <Sparkles size={18} className="text-yellow-400" />
            Join the space adventure!
            <Sparkles size={18} className="text-yellow-400" />
          </p>
        </div>

        <div className="bg-black/70 backdrop-blur-xl p-8 rounded-2xl border-2 border-purple-500/50 shadow-[0_0_60px_rgba(168,85,247,0.3)]">
          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-3 rounded-xl font-bold transition-all duration-300 ${
                isLogin 
                  ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-[0_0_30px_rgba(220,38,38,0.5)]' 
                  : 'bg-transparent text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              Login
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-3 rounded-xl font-bold transition-all duration-300 ${
                !isLogin 
                  ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-[0_0_30px_rgba(220,38,38,0.5)]' 
                  : 'bg-transparent text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              Sign Up
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border-2 border-red-500 rounded-xl text-red-200 text-sm font-semibold backdrop-blur-sm">
              {error}
            </div>
          )}

          <form onSubmit={isLogin ? handleLogin : handleSignup} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-white text-sm font-bold mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-black/50 border-2 border-purple-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-all duration-300 backdrop-blur-sm"
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>

            {/* Username (Signup only) */}
            {!isLogin && (
              <div>
                <label className="block text-white text-sm font-bold mb-2">Username</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-black/50 border-2 border-purple-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-all duration-300 backdrop-blur-sm"
                    placeholder="SpaceHopper"
                    required
                  />
                </div>
              </div>
            )}

            {/* Password */}
            <div>
              <label className="block text-white text-sm font-bold mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-black/50 border-2 border-purple-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-all duration-300 backdrop-blur-sm"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            {/* OTP (Signup only) */}
            {!isLogin && (
              <div>
                <label className="block text-white text-sm font-bold mb-2">OTP Code</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="flex-1 px-4 py-3 bg-black/50 border-2 border-purple-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-all duration-300 backdrop-blur-sm"
                    placeholder="Enter OTP"
                    required
                  />
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={otpCooldown > 0 || loading}
                    className="px-5 py-3 bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-600 text-black text-sm font-bold rounded-xl transition-all duration-300 disabled:cursor-not-allowed shadow-lg hover:shadow-yellow-500/50"
                  >
                    {otpCooldown > 0 ? `${otpCooldown}s` : otpSent ? 'Resend' : 'Send OTP'}
                  </button>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || (!isLogin && !otpSent)}
              className="w-full py-4 bg-gradient-to-r from-red-600 via-red-700 to-red-600 hover:from-red-700 hover:via-red-800 hover:to-red-700 disabled:from-gray-700 disabled:via-gray-800 disabled:to-gray-700 text-white font-black text-lg rounded-xl transition-all duration-300 shadow-[0_0_30px_rgba(220,38,38,0.5)] hover:shadow-[0_0_50px_rgba(220,38,38,0.8)] disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2 border-2 border-red-400 disabled:border-gray-600"
            >
              {loading && <Loader2 className="animate-spin" size={20} />}
              {isLogin ? '🚀 Launch Game' : '✨ Create Account'}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t-2 border-purple-500/30"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-transparent text-gray-400 font-semibold">OR</span>
            </div>
          </div>

          {/* Google Login */}
          <button
            type="button"
            onClick={handleGoogleLogin}
            className="w-full py-3 bg-white hover:bg-gray-100 text-gray-800 font-bold rounded-xl transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl border-2 border-white/50"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          {/* Back to Home */}
          <button
            onClick={() => navigate('/')}
            className="w-full mt-4 py-2 text-gray-400 hover:text-white transition-colors text-sm font-semibold"
          >
            ← Menu
          </button>
        </div>
      </div>

      {/* Float animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
      `}</style>
    </div>
  )
}

export default Login
